// Lib
import React, { useCallback, useRef } from 'react';

const MODAL_INTERACTIVE_CONTENT_SELECTOR =
    '.ImageModalV2Content-frame, .ImageModalV2-edit-overlay .image-modal-content, .ImageModalV2-drawing-overlay, .ImageModalV2PrimaryTools, .ImageModalV2SecondaryTools, .ImageModalToolbarSecondaryTools, .ImageEditorModalToolbar, .drawing-editor-toolbar-container, .FloatingPanel, .RomaPopover';

// Only Roma popovers — the drawing toolbar's colour and stroke pickers. Legacy `.FloatingPanel` is
// excluded on purpose: workspace tooltips render into one, and a tooltip left open by hovering a
// toolbar button must not keep the modal from closing.
const MODAL_TOOL_POPUP_SELECTOR = '.RomaPopover';

/**
 * Determines whether a modal click happened on image content or a toolbar control.
 */
export const isModalInteractiveContentTarget = (target: EventTarget | null) =>
    target instanceof Element && !!target.closest(MODAL_INTERACTIVE_CONTENT_SELECTOR);

/**
 * Determines whether a toolbar popup is open over the modal.
 *
 * Roma defers a popover's outside-click close until after the click has been handled, so a popup
 * dismissed by this very click is still mounted while we decide whether to close the modal.
 */
export const isModalToolPopupOpen = () => !!document.querySelector(MODAL_TOOL_POPUP_SELECTOR);

type UseImageModalV2BackgroundCloseProps = {
    close: (event?: React.MouseEvent<HTMLDivElement> | globalThis.MouseEvent) => void;
};

/**
 * Matches V1's mousedown/click pairing so drags that start inside modal content
 * don't close on release. Capture phase records the mousedown target before pan
 * handlers call stopPropagation. The outer modal close path saves drawing through its registered close callback.
 */
export const useImageModalV2BackgroundClose = ({ close }: UseImageModalV2BackgroundCloseProps) => {
    const wasMouseDownOnClosingTargetRef = useRef(false);

    const handleModalMouseDown = useCallback((event: React.MouseEvent) => {
        wasMouseDownOnClosingTargetRef.current = !isModalInteractiveContentTarget(event.target);
    }, []);

    const handleModalClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (!wasMouseDownOnClosingTargetRef.current) return;
            wasMouseDownOnClosingTargetRef.current = false;

            // An open toolbar popup takes this click as its own dismissal, the same two-step the
            // sketch modal gives its popups, so only the next background click closes the modal.
            if (isModalToolPopupOpen()) return;

            close(event);
        },
        [close],
    );

    return { handleModalMouseDown, handleModalClick };
};
